/**
 * FileMakerDataAPIConfig - FileMaker Data API source configuration
 * 
 * CHANGE HISTORY:
 * - 251201_0330 danielbeer: Created FileMaker Data API configuration UI
 */

import React, { useState } from 'react';
import type { FileMakerDataAPIConfig as DataAPIConfigType } from '../../types/FileMakerTypes';
import './DataSourceConfig.css';

export interface FileMakerDataAPIConfigProps {
  config: DataAPIConfigType;
  onChange: (config: Partial<DataAPIConfigType>) => void;
  onTest?: () => Promise<void>;
}

export function FileMakerDataAPIConfig({
  config,
  onChange,
  onTest
}: FileMakerDataAPIConfigProps) {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    recordCount?: number;
  } | null>(null);

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      await onTest?.();
      setTestResult({
        success: true,
        message: 'Connection successful',
        recordCount: 0
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Connection failed'
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="filemaker-dataapi-config">
      <div className="config-section">
        <h4>Connection Settings</h4>
        
        <div className="form-group">
          <label>Server</label>
          <input
            type="text"
            value={config.server || ''}
            onChange={(e) => onChange({ server: e.target.value })}
            placeholder="fm.example.com"
          />
          <span className="form-hint">FileMaker Server hostname or IP</span>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Database</label>
            <input
              type="text"
              value={config.database || ''}
              onChange={(e) => onChange({ database: e.target.value })}
              placeholder="Contacts"
            />
          </div>

          <div className="form-group">
            <label>Layout</label>
            <input
              type="text"
              value={config.layout || ''}
              onChange={(e) => onChange({ layout: e.target.value })}
              placeholder="ContactsAPI"
            />
          </div>
        </div>
      </div>

      <div className="config-section">
        <h4>Authentication</h4>
        
        <div className="form-group">
          <label>Username</label>
          <input
            type="text"
            value={config.authentication?.username || ''}
            onChange={(e) => onChange({ 
              authentication: { 
                username: e.target.value,
                password: config.authentication?.password || ''
              }
            })}
            placeholder="admin"
            autoComplete="username"
          />
        </div>

        <div className="form-group">
          <label>Password</label>
          <input
            type="password"
            value={config.authentication?.password || ''}
            onChange={(e) => onChange({ 
              authentication: { 
                username: config.authentication?.username || '',
                password: e.target.value 
              }
            })}
            placeholder="••••••••"
            autoComplete="current-password"
          />
          <span className="form-hint">
            ⚠️ Passwords will be stored in Profylr. External keystore support coming soon.
          </span>
        </div>
      </div>

      <div className="config-section">
        <h4>Find Request (JSON)</h4>
        
        <div className="form-group">
          <label>Find Criteria</label>
          <textarea
            value={JSON.stringify(config.query?.findRequest || [], null, 2)}
            onChange={(e) => {
              try {
                const findRequest = JSON.parse(e.target.value);
                onChange({ 
                  query: { 
                    ...config.query,
                    findRequest 
                  }
                });
              } catch (err) {
                // Invalid JSON, ignore
              }
            }}
            placeholder='[{"Status": "Active"}, {"Type": "Customer"}]'
            rows={6}
          />
          <span className="form-hint">
            Array of find request objects. Example: <code>[{"{"}Status": "Active"{"}"}]</code>
          </span>
        </div>

        <div className="form-group">
          <label>Sort (JSON)</label>
          <textarea
            value={JSON.stringify(config.query?.sort || [], null, 2)}
            onChange={(e) => {
              try {
                const sort = JSON.parse(e.target.value);
                onChange({ 
                  query: { 
                    ...config.query,
                    sort 
                  }
                });
              } catch (err) {
                // Invalid JSON, ignore
              }
            }}
            placeholder='[{"fieldName": "LastName", "sortOrder": "ascend"}]'
            rows={4}
          />
          <span className="form-hint">
            Sort order array. Example: <code>[{"{"}fieldName": "LastName", sortOrder: "ascend"{"}"}]</code>
          </span>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Limit</label>
            <input
              type="number"
              value={config.query?.limit || ''}
              onChange={(e) => onChange({ 
                query: { 
                  ...config.query,
                  limit: parseInt(e.target.value) || undefined 
                }
              })}
              placeholder="100"
              min="1"
            />
          </div>

          <div className="form-group">
            <label>Offset</label>
            <input
              type="number"
              value={config.query?.offset || ''}
              onChange={(e) => onChange({ 
                query: { 
                  ...config.query,
                  offset: parseInt(e.target.value) || undefined 
                }
              })}
              placeholder="0"
              min="0"
            />
          </div>
        </div>
      </div>

      <div className="config-section">
        <h4>Field Mapping (Optional)</h4>
        <p className="form-hint" style={{ marginBottom: '1rem' }}>
          Map FileMaker field names to dataset field names
        </p>
        
        <div className="coming-soon-banner" style={{ padding: '1rem', textAlign: 'center' }}>
          <p>🔧 Field mapping UI coming in Phase 4</p>
        </div>
      </div>

      <div className="config-actions">
        <button 
          onClick={handleTest}
          disabled={isTesting || !config.server || !config.database || !config.layout}
          className="btn btn-secondary"
        >
          {isTesting ? '⟳ Testing...' : '🔍 Test Connection'}
        </button>

        {testResult && (
          <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
            <span className="result-icon">
              {testResult.success ? '✓' : '✗'}
            </span>
            <span className="result-message">{testResult.message}</span>
            {testResult.recordCount !== undefined && (
              <span className="result-count">
                {testResult.recordCount} records
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
